/*
* SoundTouch JS audio processing library
* Copyright (c) Olli Parviainen
* Copyright (c) Ryan Berdeen
* Copyright (c) Jakub Fiala
* Copyright (c) Steve 'Cutter' Blades
*
* This library is free software; you can redistribute it and/or
* modify it under the terms of the GNU Lesser General Public
* License as published by the Free Software Foundation; either
* version 2.1 of the License, or (at your option) any later version.
*
* This library is distributed in the hope that it will be useful,
* but WITHOUT ANY WARRANTY; without even the implied warranty of
* MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
* Lesser General Public License for more details.
*
* You should have received a copy of the GNU Lesser General Public
* License along with this library; if not, write to the Free Software
* Foundation, Inc., 59 Temple Place, Suite 330, Boston, MA  02111-1307  USA
*/

import RateTransposer from './RateTransposer'
import Stretch from './Stretch'
import FifoSampleBuffer from './FifoSampleBuffer'
import testFloatEqual from './testFloatEqual'

export default class SoundTouch {
  private transposer: RateTransposer
  private stretch: Stretch
  private _inputBuffer: FifoSampleBuffer
  private _intermediateBuffer: FifoSampleBuffer
  private _outputBuffer: FifoSampleBuffer
  private _rate: number
  private _tempo: number
  private virtualPitch: number
  private virtualRate: number
  private virtualTempo: number

  constructor() {
    this.transposer = new RateTransposer()
    this.stretch = new Stretch()
    this._inputBuffer = new FifoSampleBuffer()
    this._intermediateBuffer = new FifoSampleBuffer()
    this._outputBuffer = new FifoSampleBuffer()

    this._rate = 0
    this._tempo = 0

    this.virtualPitch = 1.0
    this.virtualRate = 1.0
    this.virtualTempo = 1.0

    this.calculateEffectiveRateAndTempo()
  }

  clear() {
    this.transposer.clear()
    this.stretch.clear()
  }

  get rate() {
    return this._rate
  }

  set rate(rate) {
    this.virtualRate = rate
    this.calculateEffectiveRateAndTempo()
  }

  get tempo() {
    return this._tempo
  }

  set tempo(tempo) {
    if (this.virtualTempo !== tempo) {
      this.virtualTempo = tempo
      this.calculateEffectiveRateAndTempo()
    }
  }

  set pitch(pitch: number) {
    if (this.virtualPitch !== pitch) {
      this.virtualPitch = pitch
      this.calculateEffectiveRateAndTempo()
    }
  }

  set pitchOctaves(pitchOctaves: number) {
    this.pitch = Math.exp(0.69314718056 * pitchOctaves)
  }

  set pitchSemitones(pitchSemitones: number) {
    this.pitchOctaves = pitchSemitones / 12.0
  }

  get inputBuffer() {
    return this._inputBuffer
  }

  get outputBuffer() {
    return this._outputBuffer
  }

  calculateEffectiveRateAndTempo() {
    const previousTempo = this._tempo
    const previousRate = this._rate

    this._tempo = this.virtualTempo / this.virtualPitch
    this._rate = this.virtualRate * this.virtualPitch

    if (testFloatEqual(this._tempo, previousTempo)) {
      this.stretch.tempo = this._tempo
    }
    if (testFloatEqual(this._rate, previousRate)) {
      this.transposer.rate = this._rate
    }

    if (this._rate > 1.0) {
      if (this._outputBuffer != this.transposer.outputBuffer) {
        this.stretch.inputBuffer = this._inputBuffer
        this.stretch.outputBuffer = this._intermediateBuffer

        this.transposer.inputBuffer = this._intermediateBuffer
        this.transposer.outputBuffer = this._outputBuffer
      }
    } else {
      if (this._outputBuffer != this.stretch.outputBuffer) {
        this.transposer.inputBuffer = this._inputBuffer
        this.transposer.outputBuffer = this._intermediateBuffer

        this.stretch.inputBuffer = this._intermediateBuffer
        this.stretch.outputBuffer = this._outputBuffer
      }
    }
  }

  process() {
    if (this._rate > 1.0) {
      this.stretch.process()
      this.transposer.process()
    } else {
      this.transposer.process()
      this.stretch.process()
    }
  }
}
